<template>
    <div>
        <router-view/>
        <CookieConsentModal/>
        <ErrorMaintenance/>
        <SocketDebugOverlay v-if="socketDebugEnabled"/>
    </div>
</template>

<script>
import CookieConsentModal from '@/common/components/cookie/CookieConsentModal.vue';
import ErrorMaintenance from '@components/ErrorMaintenance.vue';
import { addScrollbarWidthToBody } from '@/utils/scrollbarUtils';
import axios from 'axios';
import {mapGetters} from 'vuex';
import { SOCKET_DEBUG_ENABLED } from '@services/socketDebug';

export default {
    data() {
        return {
            eventListenersAdded: false,
            // Not reactive state — a build-time constant, read once for v-if.
            socketDebugEnabled: SOCKET_DEBUG_ENABLED
        };
    },
    computed: {
        ...mapGetters(['language'])
    },

    methods: {
        setLanguage(lang) {
            this.$store.dispatch('SET_LANGUAGE', lang).catch(() => {
                if (lang !== 'en') {
                    this.setLanguage('en'); // infinite loop when EN not enabled for country?
                }
            });
        },
        unhandledRejection(e) {
            if (/loading chunk \d* failed./i.test(e.reason)) {
                const targetPath = this.$router.history.pending.fullPath;
                if (targetPath) {
                    window.location.pathname = targetPath;
                }
            }
        },
        checkRefreshCooldown() {
            const time = Date.now();
            const lastRefreshTime = localStorage.getItem('lastAutoRefresh');

            if (lastRefreshTime && (time - lastRefreshTime > 1000 * 60 * 60)) {
                return true;
            }

            return false;
        },
        getCachedAppVersion() {
            return localStorage.getItem('buildVersion');
        },
        reloadHandler() {
            const cachedVersion = this.getCachedAppVersion();

            if (cachedVersion && !document.hidden && navigator.onLine){
                if (this.checkRefreshCooldown()) {
                    localStorage.setItem('lastAutoRefresh', Date.now());
                    axios.get(`${window.location.origin}/version.json`)
                        .then((data) => {
                            if (data?.data?.version && data.data.version !== cachedVersion) {
                                window.location.reload();
                            }
                        })
                        .catch(() => {
                            console.warn('Failed to check for new version');
                        });
                }
            }
        }
    },
    metaInfo() {
        const title = this.$t('ELIIS - Digital diary for kindergartens');
        const description = this.$t('ELIIS is a modern early childhood education platform connecting kindergartens, teachers and families in one simple, secure digital environment.');
        const keywords = this.$t('eliis, kindergarten, daycare, pre-school');
        const lang = this.language || 'en';

        const image = `${window.location.origin}/images/Eliis_social_share.jpg`;

        return {
            htmlAttrs: {
                lang
                // TODO: Uncomment this when we have a proper RTL support
                // dir: lang === 'ar' ? 'rtl' : 'ltr',
            },
            title,
            meta: [
                {vmid: 'description', name: 'description', content: description},
                {vmid: 'keywords', name: 'keywords', content: keywords},
                {vmid: 'og:title', property: 'og:title', content: title},
                {vmid: 'og:description', property: 'og:description', content: description},
                {vmid: 'og:image', property: 'og:image', content: image},
                {vmid: 'og:image:alt', property: 'og:image:alt', content: title},
                {vmid: 'og:type', property: 'og:type', content: 'website'},
                {vmid: 'og:url', property: 'og:url', content: window.location.href},
                {vmid: 'og:site_name', property: 'og:site_name', content: 'ELIIS'},
                {vmid: 'twitter:card', name: 'twitter:card', content: 'summary_large_image'},
                {vmid: 'twitter:title', name: 'twitter:title', content: title},
                {vmid: 'twitter:description', name: 'twitter:description', content: description},
                {vmid: 'twitter:image', name: 'twitter:image', content: image}
            ]
        };
    },
    components: {
        CookieConsentModal,
        ErrorMaintenance,
        /*
         * The condition is spelled out here rather than reusing the imported
         * SOCKET_DEBUG_ENABLED on purpose. DefinePlugin substitutes
         * `process.env.VUE_APP_ENV` per module, and webpack collects `import()`
         * dependencies while parsing — long before it could fold a constant
         * imported from elsewhere. Written inline, the ternary is statically
         * false in staging and production, so the parser skips the dead branch
         * and emits no socket-debug chunk at all.
         */
        SocketDebugOverlay: (process.env.VUE_APP_ENV === 'development' || process.env.VUE_APP_ENV === 'testing')
            ? () => import(/* webpackChunkName: "socket-debug" */ '@components/debug/SocketDebugOverlay.vue')
            : null
    },
    created() {
        const lang = this.$cookies.get('lang')  || 'en';
        this.$store.dispatch('GET_LANGUAGES').then(() => {
            this.setLanguage(lang);
            this.$store.dispatch('GET_SYSTEM_AUTH_PROVIDERS');
        });

        const lastRefreshTime = localStorage.getItem('lastAutoRefresh');

        if (!lastRefreshTime) {
            localStorage.setItem('lastAutoRefresh', Date.now());
        }

        // Set build version on initial load, otherwise there is nothing to compare against
        axios.get(`${window.location.origin}/version.json`)
            .then((data) => {
                if (data?.data?.version) {
                    localStorage.setItem('buildVersion', data.data.version);
                }
            })
            .catch(() => {
                console.warn('Failed to get version on app load');
            });
    },
    mounted() {
        addScrollbarWidthToBody(); // calculate scrollbar width for modal padding to prevent content shift

        // Prevent multiple event listeners from develompent hot reload
        if (!this.eventListenersAdded) {
            window.addEventListener('unhandledrejection', this.unhandledRejection); // catch unhandledRejection from promise
            document.addEventListener('visibilitychange', this.reloadHandler);
            window.addEventListener('focus', this.reloadHandler);
            this.eventListenersAdded = true;
        }
    },
    beforeDestroy() {
        // remove on unmount
        window.removeEventListener('unhandledrejection', this.unhandledRejection);
        document.addEventListener('visibilitychange', this.reloadHandler);
        window.addEventListener('focus', this.reloadHandler);
    }
};
</script>
